import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Divider,
  Alert,
  LinearProgress,
  Stack
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  School,
  EmojiEvents,
  Edit,
  Save,
  Cancel,
  Verified,
  Token,
  TrendingUp,
  Star
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  
  // 设置页面标题
  usePageTitle('profile');
  
  const { user, updateProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    username: user?.username || '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await updateProfile(formData);
      setEditing(false);
      setSuccess('个人信息更新成功！');
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: user?.fullName || '',
      email: user?.email || '',
      username: user?.username || '',
    });
    setEditing(false);
    setError(null);
    setSuccess(null);
  };

  const achievements = [
    {
      id: 1,
      title: 'Web3.0基础认证',
      description: '完成Web3.0基础与区块链技术课程',
      date: '2024-01-15',
      type: 'certificate',
      verified: true,
    },
    {
      id: 2,
      title: 'AI基础认证',
      description: '完成人工智能与机器学习导论课程',
      date: '2024-02-20',
      type: 'certificate',
      verified: true,
    },
    {
      id: 3,
      title: '早期采用者',
      description: '前100名注册用户',
      date: '2023-12-01',
      type: 'badge',
      verified: true,
    },
    {
      id: 4,
      title: '学习达人',
      description: '连续学习30天',
      date: '2024-03-01',
      type: 'badge',
      verified: true,
    },
  ];

  const learningStats = {
    totalCourses: 3,
    completedCourses: 2,
    totalHours: 85,
    tokensEarned: 1250,
    certificatesEarned: 2,
    currentStreak: 15,
  };

  return (
    <Box>
      {/* Profile Header */}
      <Box sx={{ bgcolor: 'grey.100', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  fontSize: '3rem',
                  bgcolor: 'primary.main',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                {user?.fullName?.charAt(0) || 'U'}
              </Avatar>
              <Chip
                icon={<Verified />}
                label={user?.isVerified ? '已认证用户' : '未认证'}
                color={user?.isVerified ? 'success' : 'default'}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} md={9}>
              <Typography variant="h3" component="h1" gutterBottom>
                {user?.fullName || user?.username}
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                @{user?.username}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                角色: {user?.role === 'student' ? '学生' : user?.role === 'instructor' ? '讲师' : '管理员'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                加入时间: 2023年12月
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {/* Personal Information */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">
                  个人信息
                </Typography>
                {!editing && (
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => setEditing(true)}
                  >
                    编辑
                  </Button>
                )}
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="姓名"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    disabled={!editing}
                    variant={editing ? "outlined" : "filled"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="用户名"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    disabled={!editing}
                    variant={editing ? "outlined" : "filled"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="邮箱"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!editing}
                    variant={editing ? "outlined" : "filled"}
                  />
                </Grid>
              </Grid>

              {editing && (
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSave}
                    disabled={loading}
                  >
                    保存
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    取消
                  </Button>
                </Box>
              )}
            </Paper>

            {/* Learning Statistics */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                学习统计
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main">
                      {learningStats.completedCourses}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      已完成课程
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      {learningStats.totalHours}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      学习小时
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {learningStats.tokensEarned}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      获得代币
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="secondary.main">
                      {learningStats.certificatesEarned}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      获得证书
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main">
                      {learningStats.currentStreak}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      连续学习天数
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="text.primary">
                      {Math.round((learningStats.completedCourses / learningStats.totalCourses) * 100)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      完成率
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Achievements */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                成就与证书
              </Typography>
              
              <List>
                {achievements.map((achievement, index) => (
                  <React.Fragment key={achievement.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        {achievement.type === 'certificate' ? (
                          <EmojiEvents sx={{ color: 'warning.main' }} />
                        ) : (
                          <Token sx={{ color: 'primary.main' }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2">
                              {achievement.title}
                            </Typography>
                            {achievement.verified && (
                              <Verified sx={{ fontSize: 16, color: 'success.main' }} />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {achievement.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {achievement.date}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    {index < achievements.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProfilePage; 